import { useQuery } from '@tanstack/react-query';
import { find, isEmpty, map, startsWith } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { getChannelsColorPresets } from 'api/slideMultiplexColorPresets';
import { MultiplexMarkerInfo, MultiplexMarkersChip } from 'components/atoms/MultiplexMarkersChip';
import { getChannelColorPresetSettingsByMarkerType } from 'components/Procedure/SlideControls/Multiplex/colorSettings';
import { useOpenBulkMarkerNamingDialog } from 'components/StudyDashboard/ProceduresPage/Actions/BulkMultiplexMarkerNamingDialog/useOpenBulkMarkerNamingDialog';
import { defaultLayerColor, defaultLayerColors } from 'components/theme/theme';
import { ChannelsColorPreset } from 'interfaces/channelsPreset';
import { Slide } from 'interfaces/slide';
import { MULTIPLEX_STAIN_ID, StainType } from 'interfaces/stainType';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import useStudy from 'utils/useStudy';

import {
  getChannelSecondaryLabel,
  getMultiplexChannelNamingRows,
  getMultiplexMarkersChipCopy,
  isDnaChannel,
  MultiplexChannelNamingRow,
} from './multiplexChannelNaming';

export interface SlideMultiplexMarkersProps {
  slide?: Slide | null;
  studyId?: string;
  caseId?: string | number;
  slideId?: string;
  dismissToken?: number;
  onOpenChange?: (isOpen: boolean) => void;
  appearance?: 'card' | 'table';
}

interface SlideMultiplexMarkersContentProps extends SlideMultiplexMarkersProps {
  slide: Slide;
}

const getMarkerLabel = (row: MultiplexChannelNamingRow, stainTypeIdToDisplayName: (stainTypeId: string) => string) =>
  row.markerId ? stainTypeIdToDisplayName(row.markerId) || row.markerLabel : row.markerLabel;

const getResolvedSlideId = (slide: Slide, slideId?: string) => {
  if (slideId) {
    return slideId;
  }

  if (slide.id && !startsWith(slide.id, 'case-')) {
    return slide.id;
  }

  return undefined;
};

const SlideMultiplexMarkersContent = ({
  slide,
  studyId,
  caseId,
  slideId,
  dismissToken,
  onOpenChange,
  appearance = 'card',
}: SlideMultiplexMarkersContentProps) => {
  const { labId } = useCurrentLabId();
  const [isMarkersPopoverOpen, setIsMarkersPopoverOpen] = useState(false);
  const { stainTypeIdToDisplayName, stainTypeOptions } = useStainTypeIdToDisplayName(
    isMarkersPopoverOpen ? studyId ?? slide.studyId : undefined
  );
  const { canOpenMarkerNamingDialog, openMarkerNamingDialog } = useOpenBulkMarkerNamingDialog();

  const { study } = useStudy(studyId, {
    enabled: isMarkersPopoverOpen && Boolean(studyId),
  });
  const { data: channelPresets } = useQuery<ChannelsColorPreset[]>({
    queryKey: ['channelPresets', labId],
    queryFn: () => getChannelsColorPresets(labId),
    enabled: isMarkersPopoverOpen && Boolean(labId) && !isEmpty(slide?.channelMarkerTypes),
  });

  const studyDefaultColorPresetId = study?.settings?.defaultMultiplexColorPreset;
  const studyDefaultColorPreset = useMemo(
    () => find(channelPresets, { id: studyDefaultColorPresetId }),
    [channelPresets, studyDefaultColorPresetId]
  );

  const namingRows = useMemo(() => getMultiplexChannelNamingRows(slide, stainTypeOptions), [slide, stainTypeOptions]);
  const chipCopy = useMemo(() => getMultiplexMarkersChipCopy(namingRows), [namingRows]);
  const markerInfos = useMemo<MultiplexMarkerInfo[]>(
    () =>
      map(namingRows, (row) => {
        const channel = slide?.channels?.[row.channelId];
        const presetColor = row.markerId
          ? getChannelColorPresetSettingsByMarkerType(row.markerId, studyDefaultColorPreset?.options || {})?.color
          : undefined;

        return {
          key: `${row.channelId}-${row.markerId || row.kind}`,
          markerId: row.markerId || row.channelId,
          label: getMarkerLabel(row, stainTypeIdToDisplayName),
          secondaryLabel: getChannelSecondaryLabel(row),
          opensMarkerNamingForm: row.kind === 'missing',
          color:
            presetColor ||
            channel?.color ||
            getFallbackMarkerColor({
              channelId: row.channelId,
              stainTypeOptions,
              markerId: row.markerId,
            }),
        };
      }),
    [namingRows, slide, stainTypeIdToDisplayName, stainTypeOptions, studyDefaultColorPreset]
  );

  const openMarkerNamingForm = useCallback(
    () => openMarkerNamingDialog({ slideId: getResolvedSlideId(slide, slideId), caseId }),
    [caseId, openMarkerNamingDialog, slide, slideId]
  );

  const handleMarkersOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsMarkersPopoverOpen(isOpen);
      onOpenChange?.(isOpen);
    },
    [onOpenChange]
  );

  if (!markerInfos.length) {
    return null;
  }

  return (
    <MultiplexMarkersChip
      markerInfos={markerInfos}
      dismissToken={dismissToken}
      onOpenChange={handleMarkersOpenChange}
      appearance={appearance}
      chipLabel={chipCopy.chipLabel}
      popoverTitle={chipCopy.popoverTitle}
      guidance={chipCopy.guidance}
      emphasizeIncomplete={chipCopy.isIncomplete}
      onChipAction={chipCopy.opensMarkerNamingForm && canOpenMarkerNamingDialog ? openMarkerNamingForm : undefined}
      onMarkerAction={canOpenMarkerNamingDialog ? openMarkerNamingForm : undefined}
    />
  );
};

const TableSlideMultiplexMarkers = ({
  slide,
  studyId,
  caseId,
  slideId,
  dismissToken,
  onOpenChange,
}: Pick<
  SlideMultiplexMarkersContentProps,
  'slide' | 'studyId' | 'caseId' | 'slideId' | 'dismissToken' | 'onOpenChange'
>) => {
  const [isMarkersPopoverOpen, setIsMarkersPopoverOpen] = useState(false);
  // Grid rows only load study settings (RNA probe display names) once the popover is opened.
  const { stainTypeIdToDisplayName, stainTypeOptions } = useStainTypeIdToDisplayName(
    isMarkersPopoverOpen ? studyId ?? slide.studyId : undefined
  );
  const handleMarkersOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsMarkersPopoverOpen(isOpen);
      onOpenChange?.(isOpen);
    },
    [onOpenChange]
  );
  const { canOpenMarkerNamingDialog, openMarkerNamingDialog } = useOpenBulkMarkerNamingDialog();
  const namingRows = useMemo(() => getMultiplexChannelNamingRows(slide, stainTypeOptions), [slide, stainTypeOptions]);
  const chipCopy = useMemo(() => getMultiplexMarkersChipCopy(namingRows), [namingRows]);
  const markerInfos = useMemo<MultiplexMarkerInfo[]>(
    () =>
      map(namingRows, (row) => ({
        key: `${row.channelId}-${row.markerId || row.kind}`,
        markerId: row.markerId || row.channelId,
        label: getMarkerLabel(row, stainTypeIdToDisplayName),
        secondaryLabel: getChannelSecondaryLabel(row),
        opensMarkerNamingForm: row.kind === 'missing',
      })),
    [namingRows, stainTypeIdToDisplayName]
  );
  const openMarkerNamingForm = useCallback(
    () => openMarkerNamingDialog({ slideId: getResolvedSlideId(slide, slideId), caseId }),
    [caseId, openMarkerNamingDialog, slide, slideId]
  );

  if (!markerInfos.length) {
    return null;
  }

  return (
    <MultiplexMarkersChip
      markerInfos={markerInfos}
      dismissToken={dismissToken}
      onOpenChange={handleMarkersOpenChange}
      appearance="table"
      chipLabel={chipCopy.chipLabel}
      popoverTitle={chipCopy.popoverTitle}
      guidance={chipCopy.guidance}
      emphasizeIncomplete={chipCopy.isIncomplete}
      onChipAction={chipCopy.opensMarkerNamingForm && canOpenMarkerNamingDialog ? openMarkerNamingForm : undefined}
      onMarkerAction={canOpenMarkerNamingDialog ? openMarkerNamingForm : undefined}
    />
  );
};

const SlideMultiplexMarkers = ({
  slide,
  studyId,
  caseId,
  slideId,
  dismissToken,
  onOpenChange,
  appearance = 'card',
}: SlideMultiplexMarkersProps) => {
  if (slide?.stainingType !== MULTIPLEX_STAIN_ID) {
    return null;
  }

  if (appearance === 'table') {
    return (
      <TableSlideMultiplexMarkers
        slide={slide}
        studyId={studyId}
        caseId={caseId}
        slideId={slideId}
        dismissToken={dismissToken}
        onOpenChange={onOpenChange}
      />
    );
  }

  return (
    <SlideMultiplexMarkersContent
      slide={slide}
      studyId={studyId}
      caseId={caseId}
      slideId={slideId}
      dismissToken={dismissToken}
      onOpenChange={onOpenChange}
      appearance={appearance}
    />
  );
};

export default SlideMultiplexMarkers;

function getFallbackMarkerColor({
  channelId,
  markerId,
  stainTypeOptions,
}: {
  channelId: string;
  markerId?: string;
  stainTypeOptions?: StainType[];
}) {
  if (isDnaChannel({ markerId, stainTypeOptions })) {
    return defaultLayerColor;
  }

  const numericChannelId = Number(channelId);
  const colorIndex = Number.isFinite(numericChannelId) ? numericChannelId : 0;
  return defaultLayerColors[colorIndex % defaultLayerColors.length];
}
